// Whether a file's bytes may be served to somebody with no credential at all.
//
// WHAT THIS DECIDES, AND WHAT IT DOES NOT
//
// It gates ONE thing: whether `/file/<bucketId>/<fileId>/content/<path>` answers
// a request that carries no session cookie and no API key. It says nothing about
// the authenticated route — a signed-in member of the organization that owns the
// bucket reads any of their own files, exactly as they did before this existed.
//
// So `private` is not "hidden from the owner". It is "not on the open web".
//
// IT IS PER FILE, AND THE FIRST TWO REASONS ARE MECHANICAL
//
//   - An anonymous caller CANNOT resolve a bucket. `buckets-table` is keyed
//     `orgId + bucketId` and the orgId comes off the account; `bucket-files-table`
//     is keyed `bucketId + fileId`, both of which are in the URL. Enforcing on the
//     bucket would need a new GSI on bucketId plus a backfill before the edge
//     could read the flag at all.
//   - The file row already carries `orgId`, so a public read builds the whole
//     origin key from the one row it already fetched — no bucket lookup, which is
//     the lookup it cannot do.
//   - A bucket here is a TENANT PARTITION and not a folder. pagews provisions one
//     per organization, uuid-named, never shown to anybody; making one public
//     would publish an entire tenant's images.
//   - pagews already models this per file (@common/schema/site-file/visibility in
//     that project) and would otherwise have to shard files into two buckets by
//     visibility and MOVE THE OBJECT on every toggle.
//
// A `defaultVisibility` on the bucket — stamped onto new files, never consulted
// as the enforcement point — is the one bucket-level shape that would be safe.
// It is not built; see docs/bucket-file-visibility.md.
//
// TWO STATES AND NOT A BOOLEAN
//
// The same reason pagews' copy gives, and the vocabulary is deliberately the
// same string so the two layers read identically in a log line: there is a third
// state that can be wanted later — bytes under a signed URL with an expiry — and
// a boolean has nowhere to put it. It also reads, which `isPublic: false` does
// not.
export const PRIVATE = "private";
export const PUBLIC = "public";

export const VISIBILITIES = [PRIVATE, PUBLIC];

// UNKNOWN IS PRIVATE, AND SO IS ABSENT.
//
// Every file in this service was uploaded before this field existed, under a
// promise that a credential was needed to read it. Those rows carry no value —
// null, once datorm has serialized them — and nothing about them says anything
// about what their owner wanted published. Defaulting them to public, or
// backfilling them to public, would publish other people's files as a side
// effect of a deploy: the one outcome this whole field exists to put a person in
// charge of. There is no backfill, and none is needed.
//
// So the test is equality with the one string, never absence of the other.
export const isPublic = (row) => Boolean(row) && row.visibility === PUBLIC;

export const isVisibility = (value) => VISIBILITIES.includes(value);
